<div class="qb" [class.qb--edit]="editing">
  <!-- Advice, not a wall. A shape the zones cannot draw is shown in the
       structure view below, where it stays editable — converting it into a shape
       they could draw is what used to change what the query matched. -->
  <ng-container *ngIf="!model.representable">
    <div class="qb__banner" *ngIf="onlyInertExclusions; else shapeBanner">
      <span class="qb__banner-mark" aria-hidden="true">▲</span>
      <div class="qb__banner-body">
        <p class="qb__banner-text">
          <strong
            >{{ inertExclusions.length }}
            {{ inertExclusions.length === 1 ? 'exclusion' : 'exclusions' }}
            here currently
            {{ inertExclusions.length === 1 ? 'excludes' : 'exclude' }}
            nothing.</strong
          >
          {{ inertExclusionList }}
          {{ inertExclusions.length === 1 ? 'sits' : 'sit' }} inside an
          <em>any one of these</em> group, so an article matches when it
          <em>doesn’t</em> contain
          {{ inertExclusions.length === 1 ? 'it' : 'them' }}. Moving
          {{ inertExclusions.length === 1 ? 'it' : 'them' }} to
          <em>Never a match</em> fixes it.
        </p>
      </div>
    </div>

    <ng-template #shapeBanner>
      <div class="qb__banner">
        <span class="qb__banner-mark" aria-hidden="true">▲</span>
        <div class="qb__banner-body">
          <p class="qb__banner-text">
            This query has {{ blockerReason }}, so the zones can’t show it
            faithfully. It is drawn as it is below — every term still editable,
            nothing reshaped.
          </p>
          <p class="qb__banner-meta">{{ blockerSummary }}</p>
        </div>
      </div>
    </ng-template>

    <div class="qb__fix" *ngIf="editing && onlyInertExclusions">
      <button type="button" class="qb__btn" (click)="fixInertExclusions()">
        Move
        {{ inertExclusions.length === 1 ? 'it' : 'them' }} to Never a match
      </button>
    </div>
  </ng-container>

  <!-- Three readings of one query. Zones is disabled with its reason rather
       than hidden, so the choice stays discoverable. -->
  <div class="qb__views">
    <div
      class="qb__seg qb__seg--views"
      role="group"
      aria-label="How to show this query"
    >
      <button
        type="button"
        [class.is-on]="view === 'zones'"
        [attr.aria-pressed]="view === 'zones'"
        [disabled]="!model.representable"
        [title]="zonesReason || 'The query as blocks of interchangeable terms'"
        (click)="setView('zones')"
      >
        Zones
      </button>
      <button
        type="button"
        [class.is-on]="view === 'structure'"
        [attr.aria-pressed]="view === 'structure'"
        title="Every group exactly where it sits in the query"
        (click)="setView('structure')"
      >
        Structure
      </button>
      <button
        type="button"
        [class.is-on]="view === 'text'"
        [attr.aria-pressed]="view === 'text'"
        title="The query written out"
        (click)="setView('text')"
      >
        Text
      </button>
    </div>
  </div>

  <pre class="qb__text" *ngIf="view === 'text'">{{ queryText }}</pre>

  <app-query-node
    *ngIf="view === 'structure'"
    [node]="nodeTree"
    [editing]="editing"
    [pills]="pills"
    (nodeAction)="onNodeAction($event)"
  ></app-query-node>

  <ng-container
    *ngIf="view === 'zones'"
    [ngTemplateOutlet]="zones"
  ></ng-container>

  <ng-template #zones>
    <div class="qb__zones">
      <!-- How the blocks combine. A named choice, not a word to discover: this
           changes what the whole query means, so it looks like a control. -->
      <div class="qb__match" *ngIf="showTopJoiner">
        <span class="qb__match-label">An article must match</span>
        <div
          class="qb__seg"
          role="group"
          aria-label="How the blocks below combine"
          [title]="topJoinerHint"
        >
          <button
            type="button"
            [class.is-on]="model.joiner === 'and'"
            [attr.aria-pressed]="model.joiner === 'and'"
            [disabled]="!canSetTopJoiner && model.joiner !== 'and'"
            (click)="setTopJoinerTo('and')"
          >
            every block
          </button>
          <button
            type="button"
            [class.is-on]="model.joiner === 'or'"
            [attr.aria-pressed]="model.joiner === 'or'"
            [disabled]="!canSetTopJoiner && model.joiner !== 'or'"
            (click)="setTopJoinerTo('or')"
          >
            any one block
          </button>
        </div>
      </div>

      <p class="qb__lead">
        <ng-container *ngIf="model.joiner === 'and'"
          >Every block below is mentioned — each by <strong>any one</strong> of
          its terms</ng-container
        >
        <ng-container *ngIf="model.joiner === 'or'"
          >Any one of the blocks below is enough</ng-container
        >
        <ng-container *ngIf="model.exclusions.length">
          , and <strong>none</strong> of the exclusions appear</ng-container
        >.
      </p>

      <ng-container
        *ngFor="let group of model.groups; let i = index; trackBy: trackByGroup"
      >
        <div class="qb__joiner" *ngIf="i">
          <span>{{ joinerLabel }}</span>
        </div>

        <section
          class="qb-group"
          [class.qb-group--grounded]="group.context"
          [class.qb-group--incomplete]="isIncomplete(group)"
        >
          <header class="qb-group__head">
            <span class="qb-group__num">{{ i + 1 }}</span>
            <span class="qb-group__title">{{ title(group, i) }}</span>
            <span class="qb-group__kind" *ngIf="groupKind(group) as kind">{{
              kind
            }}</span>
            <!-- What this group asserts, as a named choice rather than a word
                 the user has to discover is clickable. -->
            <div
              class="qb__seg qb__seg--sm"
              *ngIf="canSetJoiner(i); else plainNote"
              role="group"
              aria-label="What this group asserts"
            >
              <button
                type="button"
                [class.is-on]="group.joiner === 'any'"
                [attr.aria-pressed]="group.joiner === 'any'"
                title="One of these terms is enough"
                (click)="setGroupJoinerTo(group, 'any')"
              >
                any one
              </button>
              <button
                type="button"
                [class.is-on]="group.joiner === 'all'"
                [attr.aria-pressed]="group.joiner === 'all'"
                title="Every one of these terms has to appear"
                (click)="setGroupJoinerTo(group, 'all')"
              >
                all
              </button>
            </div>
            <ng-template #plainNote>
              <span class="qb-group__note">{{ note(group) }}</span>
            </ng-template>
            <span class="qb-group__count">{{ termCount(group) }}</span>

            <span class="qb-group__ctrls" *ngIf="editing">
              <!-- Always offered: an affordance that appears on some groups and
                   not others reads as arbitrary. What cannot run is disabled and
                   says why. -->
              <button
                type="button"
                class="qb-group__menu"
                [matMenuTriggerFor]="groupMenu"
                title="Restructure"
              >
                ⋮
              </button>
              <mat-menu #groupMenu="matMenu" class="qb-menu">
                <ng-template matMenuContent>
                  <button
                    mat-menu-item
                    [disabled]="!canSplit(i)"
                    (click)="split(i)"
                  >
                    <span class="qb-menu__title"
                      >Split into separate groups</span
                    >
                    <span class="qb-menu__note">{{
                      splitReason(i) ||
                        'each term becomes required — OR turns into AND'
                    }}</span>
                  </button>
                  <button
                    mat-menu-item
                    [disabled]="!canMerge(i, 'up')"
                    (click)="merge(i, 'up')"
                  >
                    <span class="qb-menu__title"
                      >Merge into the group above</span
                    >
                    <span class="qb-menu__note">{{
                      mergeReason(i, 'up') ||
                        'both term lists become alternatives — AND turns into OR'
                    }}</span>
                  </button>
                  <button
                    mat-menu-item
                    [disabled]="!canMerge(i, 'down')"
                    (click)="merge(i, 'down')"
                  >
                    <span class="qb-menu__title"
                      >Merge into the group below</span
                    >
                    <span class="qb-menu__note">{{
                      mergeReason(i, 'down') ||
                        'both term lists become alternatives — AND turns into OR'
                    }}</span>
                  </button>
                </ng-template>
              </mat-menu>
              <button
                type="button"
                class="qb-group__x"
                (click)="dropGroup(group)"
                title="Remove this group"
              >
                ✕
              </button>
            </span>
          </header>

          <ng-container
            *ngTemplateOutlet="groupBody; context: { group: group }"
          ></ng-container>
        </section>
      </ng-container>

      <ng-container *ngFor="let artifact of model.artifacts">
        <div class="qb__joiner" *ngIf="model.groups.length">
          <span>{{ artifactJoinerLabel }}</span>
        </div>
        <section class="qb-group qb-group--artifact">
          <header class="qb-group__head">
            <span class="qb-pill__key">{{ artifactLabel(artifact) }}</span>
            <span class="qb-group__title">{{ artifact.name }}</span>
            <span class="qb-group__note">everything it matches counts</span>
            <span class="qb-group__ctrls" *ngIf="editing">
              <button
                type="button"
                class="qb-group__x"
                (click)="dropArtifact(artifact)"
                title="Remove"
              >
                ✕
              </button>
            </span>
          </header>
        </section>
      </ng-container>

      <div class="qb__adders" *ngIf="editing">
        <button
          type="button"
          class="qb__btn qb__btn--ghost"
          *ngIf="allowGroups"
          (click)="addRequiredGroup()"
        >
          + group
          <span class="qb__btn-note">(must also be mentioned)</span>
        </button>
        <button
          type="button"
          class="qb__btn qb__btn--ghost"
          (click)="addAmbiguousForm()"
        >
          + ambiguous form
        </button>
        <!-- One picker per kind: a single list mixed the two, and a user
             looking for a portfolio had to read past their saved queries. -->
        <span class="qb-add" *ngIf="savedQueries.length">
          <button
            type="button"
            class="qb__btn qb__btn--ghost"
            *ngIf="pickerAt !== 'artifact:query'"
            (click)="openPicker('artifact:query')"
          >
            + saved query
          </button>
          <app-term-picker
            *ngIf="pickerAt === 'artifact:query'"
            [fields]="[]"
            [artifacts]="savedQueries"
            [panelClass]="panelClass"
            placeholder="Search your saved queries"
            browseNote="Your saved queries — type to filter."
            (pick)="commitArtifact($event)"
            (dismiss)="closePicker()"
          ></app-term-picker>
        </span>
        <span class="qb-add" *ngIf="portfolios.length">
          <button
            type="button"
            class="qb__btn qb__btn--ghost"
            *ngIf="pickerAt !== 'artifact:portfolio'"
            (click)="openPicker('artifact:portfolio')"
          >
            + portfolio
          </button>
          <app-term-picker
            *ngIf="pickerAt === 'artifact:portfolio'"
            [fields]="[]"
            [artifacts]="portfolios"
            [panelClass]="panelClass"
            placeholder="Search your portfolios"
            browseNote="Your portfolios — type to filter."
            (pick)="commitArtifact($event)"
            (dismiss)="closePicker()"
          ></app-term-picker>
        </span>
      </div>

      <section
        class="qb-group qb-group--excluded"
        *ngIf="model.exclusions.length || editing"
      >
        <header class="qb-group__head">
          <span class="qb-group__title">Never a match</span>
          <span class="qb-group__note">
            any one of these disqualifies the article
          </span>
          <span class="qb-group__count">{{
            model.exclusions.length + ' excluded'
          }}</span>
        </header>

        <div class="qb-pills" *ngIf="model.excludedArtifacts.length">
          <span
            class="qb-pill qb-pill--negated"
            *ngFor="let artifact of model.excludedArtifacts"
          >
            <span class="qb-pill__key">{{ artifactLabel(artifact) }}</span>
            <span class="qb-pill__input">{{ artifact.name }}</span>
            <button
              type="button"
              class="qb-pill__x"
              *ngIf="editing"
              (click)="dropArtifact(artifact)"
              title="Remove"
            >
              ✕
            </button>
          </span>
        </div>

        <ng-container
          *ngTemplateOutlet="
            pills;
            context: {
              terms: model.exclusions,
              path: null,
              slot: 'excluded',
              zone: key(null, 'excluded'),
              placeholder: 'A term to keep out',
            }
          "
        ></ng-container>
      </section>
    </div>
  </ng-template>

  <!-- Every nested panel opens the same way: what the block is, how its terms
       combine, how many there are. -->
  <ng-template #nestHead let-lead="lead" let-note="note" let-count="count">
    <header class="qb-group__nest-head">
      <span class="qb-group__nest-lead">{{ lead }}</span>
      <span class="qb-group__note">{{ note }}</span>
      <span class="qb-group__count">{{ count }}</span>
    </header>
  </ng-template>

  <!-- The body of a group at any depth: its terms, the context that grounds
       them, its scoped exclusions, and the groups nested below it. Rendered
       through itself for the nested ones, so a sub-lane is drawn by exactly the
       markup that draws a top-level group — no depth is a special case. -->
  <ng-template #groupBody let-group="group">
    <ng-container
      *ngTemplateOutlet="
        pills;
        context: {
          terms: group.terms,
          path: group.path,
          group: group,
          slot: group.context ? 'ambiguous' : 'terms',
          zone: key(group.path, group.context ? 'ambiguous' : 'terms'),
          placeholder: 'Search or type a term',
        }
      "
    ></ng-container>

    <!-- A term too broad on its own, and what has to appear beside it.
             A panel with its own logic and its own count, because that is what
             it is — the one-line sub-row said none of that. -->
    <section
      class="qb-group__nest"
      [class.qb-group__nest--warn]="isIncomplete(group)"
      *ngIf="group.context as context"
    >
      <ng-container
        *ngTemplateOutlet="
          nestHead;
          context: {
            lead: contextLead(group),
            note: note(context),
            count: termCount(context),
          }
        "
      ></ng-container>
      <ng-container
        *ngTemplateOutlet="
          pills;
          context: {
            terms: context.terms,
            path: context.path,
            slot: 'context',
            zone: key(context.path, 'context'),
            placeholder: 'A term that has to appear too',
          }
        "
      ></ng-container>
    </section>

    <!-- Exclusions scoped to this group's terms, not query-wide. -->
    <section
      class="qb-group__nest qb-group__nest--guard"
      *ngIf="group.guard as guard"
    >
      <ng-container
        *ngTemplateOutlet="
          nestHead;
          context: {
            lead: 'except when the article also mentions',
            note: 'any one of these rules it out',
            count: termCount(guard),
          }
        "
      ></ng-container>
      <ng-container
        *ngTemplateOutlet="
          pills;
          context: {
            terms: guard.terms,
            path: guard.path,
            slot: 'guard',
            zone: key(guard.path, 'guard'),
            placeholder: 'A sense to keep out',
          }
        "
      ></ng-container>
    </section>

    <!-- A group below a group (rule 7's sub-lane, rule 8's AND of sub-lanes).
         Same nested panel as the context block above: it is the same statement
         one level further in. Structurally fixed — the joiner toggle and the
         restructure menu live in a top-level group's header, which a sub-lane
         does not get, so the only edits here are the chips themselves. -->
    <section
      class="qb-group__nest qb-group__nest--sub"
      *ngFor="let sub of group.subs; trackBy: trackByGroup"
      [class.qb-group__nest--warn]="isIncomplete(sub)"
    >
      <ng-container
        *ngTemplateOutlet="
          nestHead;
          context: {
            lead: subLead(group),
            note: note(sub),
            count: termCount(sub),
          }
        "
      ></ng-container>
      <ng-container
        *ngTemplateOutlet="groupBody; context: { group: sub }"
      ></ng-container>
    </section>
  </ng-template>

  <!-- One pill list, used by every zone: terms, context, guards, exclusions.
       `path` is where a picked term is written; null means the exclusions zone,
       which may not have a node yet. -->
  <ng-template
    #pills
    let-terms="terms"
    let-path="path"
    let-slot="slot"
    let-zone="zone"
    let-group="group"
    let-placeholder="placeholder"
    let-addOnly="addOnly"
    let-negate="negate"
    let-label="label"
  >
    <div class="qb-pills">
      <span
        class="qb-pill"
        [ngClass]="termClass(term, slot)"
        *ngFor="
          let term of addOnly ? [] : shown(terms, zone);
          trackBy: trackByTerm
        "
      >
        <span class="qb-pill__key" *ngIf="fieldLabel(term) as label">{{
          label
        }}</span>
        <input
          class="qb-pill__input"
          [value]="term.label"
          [readOnly]="!isEditable(term)"
          [style.width]="pillWidth(term.label)"
          (change)="rename(term, $any($event.target).value)"
        />
        <span class="qb-pill__vol" *ngIf="!editing && term.volume != null">{{
          volLabel(term.volume)
        }}</span>
        <button
          type="button"
          class="qb-pill__x"
          *ngIf="editing"
          (click)="remove(term, group)"
          title="Remove"
        >
          ✕
        </button>
      </span>

      <button
        type="button"
        class="qb-pills__more"
        *ngIf="!addOnly && hidden(terms, zone) as more"
        (click)="expand(zone)"
      >
        + {{ more }} more ▾
      </button>

      <span class="qb-add" [ngClass]="addClass(slot)" *ngIf="editing">
        <button
          type="button"
          class="qb-add__btn"
          *ngIf="pickerAt !== zone"
          (click)="openPicker(zone)"
        >
          {{ label || '+' }}
        </button>
        <app-term-picker
          *ngIf="pickerAt === zone"
          [fields]="fields"
          [panelClass]="panelClass"
          [placeholder]="placeholder"
          (pick)="path ? commit(path, $event, negate) : commitExclusion($event)"
          (dismiss)="closePicker()"
        ></app-term-picker>
      </span>
    </div>
  </ng-template>
</div>
